/**
 * Studio / Client Mode client hooks (moat plan §5). Shapes follow docs/moat/CONTRACT.md "Phase 3 schema"
 * and the live routes in `src/routes/api/{workspaces,invite,client,plan,public}`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '#/lib/api'
import type { EstimateLine, MemberRole, PhaseApprovalSummary, TaskTier } from '#/lib/types'
import type { OriginViewDto } from '#/lib/api-origin'

/** query keys owned by this file (`qk` in api.ts stays untouched) */
export const sqk = {
  members: (workspaceId: string) => ['studio', 'members', workspaceId] as const,
  estimate: (planId: string) => ['studio', 'estimate', planId] as const,
  proposals: (planId: string) => ['studio', 'proposals', planId] as const,
  invite: (token: string) => ['studio', 'invite', token] as const,
  activity: (workspaceId: string) => ['team', 'activity', workspaceId] as const,
  comments: (planId: string, target?: string) => ['team', 'comments', planId, target ?? '*'] as const,
  clientPlans: ['client', 'plans'] as const,
  clientPlan: (planId: string) => ['client', 'plan', planId] as const,
}

/** Studio seat cap for `member` invites (server: `MAX_SEATS` in studio.server.ts). Clients are unlimited. */
export const MAX_SEATS = 5
export const SEGMENTS = ['agency', 'freelancer', 'campus', 'other'] as const
export type WaitlistSegment = (typeof SEGMENTS)[number]

// ---------------------------------------------------------------- types
export interface Branding {
  name?: string
  logoUrl?: string
  color?: string
  contact?: string
  website?: string
}
export interface MemberDto {
  id: string
  email: string
  role: MemberRole
  userId: string | null
  acceptedAt: string | null
  createdAt: string
  name: string | null
  image: string | null
}
export interface MembersPayload {
  members: MemberDto[]
  branding: Branding | null
  /** team layer F0: seats used (member + facilitator) vs the owner's tier cap (-1 = unlimited) */
  seats?: { used: number; limit: number; planType: string }
}
export type InviteRole = 'member' | 'client' | 'facilitator'
export interface ActivityEvent {
  id: string
  name: string
  planId: string | null
  props: Record<string, unknown> | null
  createdAt: string
  actor: string | null
  actorImage: string | null
}
export interface PlanCommentDto {
  id: string
  target: string
  body: string
  createdAt: string
  userId: string
  author: string | null
  authorImage: string | null
}
export interface EstimateDto {
  id: string
  planId: string
  rateCurrency: string
  ratePerHour: number
  bufferPct: number
  breakdown: EstimateLine[]
  /** numeric(10,2) — arrives as a string */
  totalHoursMin: string
  totalHoursMax: string
  totalMin: number
  totalMax: number
  createdAt: string
  updatedAt: string
}
export type TierMedians = Partial<Record<TaskTier, number>>
export interface EstimatePayload {
  estimate: EstimateDto | null
  medians: TierMedians
}
export interface EstimateInput {
  rateCurrency: 'IDR' | 'USD'
  ratePerHour: number
  bufferPct: number
  overrides?: Record<string, number>
}
export interface ProposalDto {
  id: string
  version: number
  locale: 'en' | 'id'
  pdfPath?: string | null
  pdf?: boolean
  sentAt?: string | null
  createdAt: string
}
export interface InvitePreview {
  role: MemberRole
  email: string
  accepted: boolean
  workspaceName: string
  branding: Branding | null
}
export interface ClientPlanListItem {
  id: string
  title: string
  workspaceName: string
  role: MemberRole
  branding: Branding | null
  updatedAt: string
  cycle: number
}
export interface ClientFeature {
  id: string
  name: string
  summary: string | null
  phase: number
  icon: string | null
  tasks: number
  done: number
  verified: number
  override: number
  failed: number
  exceptions: { title: string; reason: string | null }[]
  estimate: EstimateLine | null
  actualHours: number | null
}
export interface ClientApproval {
  phase: number
  layer: string
  approvedBy: string
  createdAt: string
  summary: PhaseApprovalSummary
}
export interface ClientProgress {
  role: MemberRole
  workspace: { name?: string; branding: Branding | null }
  plan: { id: string; title: string; language: 'en' | 'id'; updatedAt: string }
  features: ClientFeature[]
  approvals: ClientApproval[]
  pending: { phase: number; layer: 'frontend' | 'backend'; taskRef?: string; summary: PhaseApprovalSummary } | null
  estimate: { currency: string; totalMin: number; totalMax: number; hoursMin: number; hoursMax: number } | null
  actual: { hours: number; verified: number; tasks: number } | null
  wikiVersion: number | null
  /** Proof-of-Why (F2): discovery agreement, null when the plan did not come from a room */
  origin?: OriginViewDto | null
}

// ---------------------------------------------------------------- members & branding
/** GET /api/workspaces/:id/members → members + workspace branding (owner only). */
export function useMembers(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: sqk.members(workspaceId),
    queryFn: () => api<MembersPayload>(`/api/workspaces/${workspaceId}/members`),
    enabled: enabled && Boolean(workspaceId),
  })
}

/** POST /api/workspaces/:id/members {email, role} → {member, inviteUrl}; 402 `seats_exhausted` when the tier's seats are used up. */
export function useInviteMember(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; role: InviteRole }) => api<{ member: MemberDto; inviteUrl: string }>(`/api/workspaces/${workspaceId}/members`, { method: 'POST', json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sqk.members(workspaceId) })
    },
  })
}

/** DELETE /api/workspaces/:id/members?memberId= */
export function useRemoveMember(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) => api<{ ok: true }>(`/api/workspaces/${workspaceId}/members?memberId=${encodeURIComponent(memberId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sqk.members(workspaceId) })
    },
  })
}

/** PATCH /api/workspaces/:id/members {branding} */
export function useSetBranding(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (branding: Branding) => api<{ branding: Branding | null }>(`/api/workspaces/${workspaceId}/members`, { method: 'PATCH', json: { branding } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sqk.members(workspaceId) })
    },
  })
}

// ---------------------------------------------------------------- team layer F0: activity & comments
/** GET /api/workspaces/:id/activity → last 50 product events of the workspace. */
export function useActivity(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: sqk.activity(workspaceId),
    queryFn: () => api<{ events: ActivityEvent[] }>(`/api/workspaces/${workspaceId}/activity`),
    enabled: enabled && Boolean(workspaceId),
    refetchInterval: 30_000,
  })
}

/** GET /api/plan/:planId/comments[?target=] */
export function usePlanComments(planId: string, target?: string, enabled = true) {
  return useQuery({
    queryKey: sqk.comments(planId, target),
    queryFn: () => api<{ comments: PlanCommentDto[] }>(`/api/plan/${planId}/comments${target ? `?target=${encodeURIComponent(target)}` : ''}`),
    enabled: enabled && Boolean(planId),
  })
}

/** POST /api/plan/:planId/comments {target, body} */
export function useAddPlanComment(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { target: string; body: string }) => api<{ comment: PlanCommentDto }>(`/api/plan/${planId}/comments`, { method: 'POST', json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team', 'comments', planId] })
    },
  })
}

/** DELETE /api/plan/:planId/comments?id= */
export function useDeletePlanComment(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/api/plan/${planId}/comments?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team', 'comments', planId] })
    },
  })
}

// ---------------------------------------------------------------- invite
/** GET /api/invite/:token — public preview of an invite. */
export function useInvitePreview(token: string) {
  return useQuery({
    queryKey: sqk.invite(token),
    queryFn: () => api<{ invite: InvitePreview }>(`/api/invite/${token}`),
    enabled: Boolean(token),
    retry: false,
  })
}

/** POST /api/invite/:token — accept (requires a session). */
export function useAcceptInvite(token: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ member: { id: string; workspaceId: string; role: MemberRole } }>(`/api/invite/${token}`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sqk.clientPlans })
      void qc.invalidateQueries({ queryKey: sqk.invite(token) })
    },
  })
}

// ---------------------------------------------------------------- estimate
/** GET /api/plan/:planId/estimate → { estimate, medians }. */
export function useEstimate(planId: string, enabled = true) {
  return useQuery({
    queryKey: sqk.estimate(planId),
    queryFn: () => api<EstimatePayload>(`/api/plan/${planId}/estimate`),
    enabled: enabled && Boolean(planId),
  })
}

/** POST /api/plan/:planId/estimate — recompute with rate / buffer / per-feature overrides. */
export function useSaveEstimate(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EstimateInput) => api<{ estimate: EstimateDto & { medians?: TierMedians } }>(`/api/plan/${planId}/estimate`, { method: 'POST', json: body }),
    onSuccess: (r) => {
      qc.setQueryData(sqk.estimate(planId), (prev: EstimatePayload | undefined) => {
        const { medians, ...row } = r.estimate
        return { estimate: row as EstimateDto, medians: medians ?? prev?.medians ?? {} }
      })
    },
  })
}

// ---------------------------------------------------------------- proposal
/** GET /api/plan/:planId/proposal → version list (newest first). */
export function useProposals(planId: string, enabled = true) {
  return useQuery({
    queryKey: sqk.proposals(planId),
    queryFn: () => api<{ proposals: ProposalDto[] }>(`/api/plan/${planId}/proposal`),
    enabled: enabled && Boolean(planId),
  })
}

/** POST /api/plan/:planId/proposal {locale, client, branding?} → new version. */
export function useCreateProposal(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { locale: 'en' | 'id'; client?: string; branding?: Branding }) => api<{ proposal: ProposalDto }>(`/api/plan/${planId}/proposal`, { method: 'POST', json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sqk.proposals(planId) })
    },
  })
}

/** URLs the proposal route serves directly (opened in a new tab, never fetched as JSON). */
export const proposalPreviewUrl = (planId: string, locale: 'en' | 'id', client?: string) => `/api/plan/${planId}/proposal?preview=1&locale=${locale}${client ? `&client=${encodeURIComponent(client)}` : ''}`
export const proposalFileUrl = (planId: string, id: string, format: 'pdf' | 'html') => `/api/plan/${planId}/proposal?id=${id}&format=${format}`
export const handoverUrl = (planId: string, locale: 'en' | 'id') => `/api/plan/${planId}/handover?locale=${locale}`

// ---------------------------------------------------------------- client mode
/** GET /api/client/plans — plans the signed-in user follows as client/member. */
export function useClientPlans(enabled = true) {
  return useQuery({ queryKey: sqk.clientPlans, queryFn: () => api<{ plans: ClientPlanListItem[] }>('/api/client/plans'), enabled })
}

/** GET /api/client/plan/:id — progress, proof counts, approvals, pending checkpoint. */
export function useClientPlan(planId: string, enabled = true) {
  return useQuery({
    queryKey: sqk.clientPlan(planId),
    queryFn: () => api<ClientProgress>(`/api/client/plan/${planId}`),
    enabled: enabled && Boolean(planId),
    refetchInterval: 30_000,
  })
}

/** POST /api/client/plan/:id — role `client` (or the owner) approves a phase. */
export function useClientApprove(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { phase: number; layer?: 'all' | 'frontend' | 'backend'; note?: string }) => api<{ approval: ClientApproval; workspaceId: string }>(`/api/client/plan/${planId}`, { method: 'POST', json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sqk.clientPlan(planId) })
    },
  })
}

// ---------------------------------------------------------------- waitlist (public)
export interface WaitlistInput {
  email: string
  name?: string
  company?: string
  segment?: WaitlistSegment
  note?: string
  locale?: 'en' | 'id'
}
/** POST /api/public/studio-waitlist — rate limited 10/h per IP. */
export const joinStudioWaitlist = (body: WaitlistInput) => api<{ ok: true }>('/api/public/studio-waitlist', { method: 'POST', json: body })
